import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { ArrowLeft, User, Users } from 'lucide-react';
import { LanguageSelector } from '../components/LanguageSelector';
import { MyErotikaLogo } from '../components/MyErotikaLogo';

const genders = [
  { id: 'woman', icon: User, color: 'from-pink-500 to-rose-500' },
  { id: 'man', icon: User, color: 'from-blue-500 to-indigo-500' },
  { id: 'couple', icon: Users, color: 'from-purple-500 to-fuchsia-500' }
];

export default function SelectGenderPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t, language } = useLanguage();

  const genderLabels = {
    woman: { fr: 'Une Femme', en: 'A Woman', es: 'Una Mujer', de: 'Eine Frau', pt: 'Uma Mulher' },
    man: { fr: 'Un Homme', en: 'A Man', es: 'Un Hombre', de: 'Ein Mann', pt: 'Um Homem' },
    couple: { fr: 'Un Couple', en: 'A Couple', es: 'Una Pareja', de: 'Ein Paar', pt: 'Um Casal' }
  };

  const genderDescriptions = {
    woman: { fr: 'Conversations avec une partenaire féminine', en: 'Conversations with a female partner', es: 'Conversaciones con una compañera femenina', de: 'Gespräche mit einer weiblichen Partnerin', pt: 'Conversas com uma parceira feminina' },
    man: { fr: 'Conversations avec un partenaire masculin', en: 'Conversations with a male partner', es: 'Conversaciones con un compañero masculino', de: 'Gespräche mit einem männlichen Partner', pt: 'Conversas com um parceiro masculino' },
    couple: { fr: 'Conversations avec un couple complice', en: 'Conversations with a playful couple', es: 'Conversaciones con una pareja cómplice', de: 'Gespräche mit einem verspielten Paar', pt: 'Conversas com um casal cúmplice' }
  };

  const handleSelect = (gender) => {
    navigate(`/select-character/${gender}`);
  };

  return (
    <div className="min-h-screen bg-velvet-bg">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-velvet_text-secondary hover:text-velvet_text-accent transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft size={20} />
            <MyErotikaLogo size="sm" />
          </button>
          <div className="flex items-center gap-4">
            <LanguageSelector />
            <div className="flex items-center gap-3">
              <span className="text-velvet_text-secondary text-sm">
                {user?.username}
                {user?.is_premium && (
                  <span className="ml-2 px-2 py-0.5 bg-premium/20 text-premium text-xs rounded-full">
                    Premium
                  </span>
                )}
              </span>
              <Button 
                variant="ghost" 
                onClick={logout}
                className="text-velvet_text-secondary hover:text-velvet_text-primary text-sm"
                data-testid="logout-button"
              >
                {t('logout')}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-4">
            <span className="text-velvet_text-accent text-sm font-medium tracking-wider uppercase">
              {({fr: 'Étape 1 sur 4', en: 'Step 1 of 4', es: 'Paso 1 de 4', de: 'Schritt 1 von 4', pt: 'Passo 1 de 4'})[language] || 'Step 1 of 4'}
            </span>
          </div>
          <h1 className="font-heading text-4xl lg:text-5xl text-center gradient-text mb-4 animate-slideUp">
            {({fr: 'Qui souhaitez-vous rencontrer ?', en: 'Who would you like to meet?', es: '¿A quién te gustaría conocer?', de: 'Wen möchtest du kennenlernen?', pt: 'Quem gostarias de conhecer?'})[language] || 'Who would you like to meet?'}
          </h1>
          <p className="text-velvet_text-secondary text-center mb-16 max-w-xl mx-auto">
            {({fr: 'Choisissez le type de partenaire pour vos conversations', en: 'Choose the type of partner for your conversations', es: 'Elige el tipo de pareja para tus conversaciones', de: 'Wähle die Art des Partners für deine Gespräche', pt: 'Escolhe o tipo de parceiro para as tuas conversas'})[language] || 'Choose the type of partner for your conversations'}
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            {genders.map((gender, index) => (
              <button
                key={gender.id}
                onClick={() => handleSelect(gender.id)}
                className={`group relative glass rounded-2xl p-8 card-hover animate-slideUp stagger-${index + 1} text-center`}
                data-testid={`gender-card-${gender.id}`}
              >
                {/* Icon */}
                <div className={`w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br ${gender.color} flex items-center justify-center opacity-80 group-hover:opacity-100 transition-opacity group-hover:scale-110 transition-transform duration-300`}>
                  <gender.icon className="text-white" size={40} />
                </div>
                
                {/* Title */}
                <h3 className="font-heading text-2xl text-velvet_text-primary mb-2">
                  {genderLabels[gender.id][language] || genderLabels[gender.id].en}
                </h3>
                
                {/* Description */}
                <p className="text-velvet_text-secondary text-sm">
                  {genderDescriptions[gender.id][language] || genderDescriptions[gender.id].en}
                </p>

                {/* Hover effect */}
                <div className="absolute inset-0 border-2 border-transparent group-hover:border-purple-main/30 rounded-2xl transition-colors" />
              </button>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
